import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useEffect, useState } from "react";

const WINDOW_MS = 1000;

type WindowLabel = "buffering" | "running";

/**
 * Remotion behaves inconsistently when it comes to buffering. It will either
 * buffer for a long time in one go, or jump between buffered and not buffered
 * states very quickly.
 *
 * This hook detects when the player is buffering for a long time in one go,
 * and shows the buffering banner.
 */

export function useBufferingBanner() {
  const getPlayerRef = useEditorStore((state) => state.service.getPlayerRef);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // This assumes that the player is always mounted before the hook is used,
    // and that when the player unmounts, the hook unmounts as well.
    const player = getPlayerRef()?.current;

    if (!player) {
      return;
    }

    let interval: ReturnType<typeof setInterval> | null = null;
    let isBuffering = false;
    let bufferedInWindow = false;
    let prevWindow: WindowLabel | null = null;

    const stop = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }

      prevWindow = null;
    };

    const onWindowEnd = () => {
      const currentWindow: WindowLabel =
        player.isPlaying() && (isBuffering || bufferedInWindow)
          ? "buffering"
          : "running";
      bufferedInWindow = false;

      if (prevWindow === currentWindow) {
        if (currentWindow === "buffering") {
          setIsVisible(true);
        } else if (currentWindow === "running") {
          setIsVisible(false);
          stop();
          return;
        }
      }

      prevWindow = currentWindow;
    };

    const handleWaiting = () => {
      isBuffering = true;
      bufferedInWindow = true;

      if (interval === null) {
        interval = setInterval(onWindowEnd, WINDOW_MS);
      }
    };

    const handleResume = () => {
      isBuffering = false;
    };

    player.addEventListener("waiting", handleWaiting);
    player.addEventListener("resume", handleResume);

    return () => {
      stop();
      player.removeEventListener("waiting", handleWaiting);
      player.removeEventListener("resume", handleResume);
    };
  }, [getPlayerRef]);

  return isVisible;
}
